'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';
import type { EditMobileNumberFormData } from '../types/edit-mobile-number';

const useEditMobileNumber = () => {
  const t = useTranslations('form');
  const phoneConfig = getSiteConfig().PHONE_NUMBER;

  const formSchema = z.object({
    mobileNumber: z
      .object({
        prefix: z.string().min(1, t('validation.phoneNumber.prefix.required')),
        number: z.string().min(1, t('validation.phoneNumber.number.required')),
      })
      .refine(
        data => {
          if (!data.prefix || !data.number) {
            return true;
          }

          const nationalDigits = data.number.replace(/\D/g, '');
          if (phoneConfig.nationalNumberRegex) {
            return phoneConfig.nationalNumberRegex.test(nationalDigits);
          }

          try {
            const fullPhoneNumber = `+${data.prefix.split('+')[1]}${data.number}`;
            return isValidPhoneNumber(fullPhoneNumber);
          } catch {
            return false;
          }
        },
        {
          message: t('validation.phoneNumber.invalid'),
          path: ['number'],
        }
      ),
    verificationCode: z.string().min(1, t('validation.verificationCode.required')).min(6, t('validation.verificationCode.minLength')),
    verificationId: z.string(),
  });

  const formMethods = useForm<EditMobileNumberFormData>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      mobileNumber: {
        prefix: '',
        number: '',
      },
      verificationCode: '',
      verificationId: '',
    },
  });

  const backHandlerRef = useRef<(() => boolean) | null>(null);

  const handleBackNavigation = (onClose: () => void) => {
    if (backHandlerRef.current) {
      const handled = backHandlerRef.current();
      if (!handled) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const registerBackHandler = (handler: () => boolean) => {
    backHandlerRef.current = handler;
  };

  return {
    formMethods,
    handleBackNavigation,
    registerBackHandler,
  };
};

export { useEditMobileNumber };
